import { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import {
  ShoppingBag, FlaskConical, Package, Loader2, X, ShoppingCart, Plus, Minus, Trash2,
  Search, User, Tag, Wallet, FileText, Lock, Building2,
} from 'lucide-react';
import API from '../../lib/api';
import AddressPickerCheckout from '../../pages/AddressPickerCheckout';

const CART_KEY = 'labs_store_cart';

export default function LabsStoreCatalogTab({ items, banners, popup, loading, onPurchased }) {
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem(CART_KEY) || '[]'); } catch { return []; }
  });
  const [showCart, setShowCart] = useState(false);
  const [showOrderDialog, setShowOrderDialog] = useState(false);
  const [showPatientSelect, setShowPatientSelect] = useState(false);
  const [patients, setPatients] = useState([]);
  const [placingOrder, setPlacingOrder] = useState(false);
  const [showPopup, setShowPopup] = useState(!!popup);
  const [bannerIdx, setBannerIdx] = useState(0);
  const [activeVendor, setActiveVendor] = useState('all');
  const [activeType, setActiveType] = useState('all');
  const [search, setSearch] = useState('');
  const [walletBalance, setWalletBalance] = useState(0);
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [appliedCoupons, setAppliedCoupons] = useState({});
  const [validatingCoupon, setValidatingCoupon] = useState(false);

  const [orderDetails, setOrderDetails] = useState({
    patient_id: '', patient_name: '', patient_phone: '', patient_email: '',
    patient_age: '', patient_gender: '', delivery_address: '', delivery_phone: '',
    delivery_location: null, notes: '', apply_gst: false, payment_method: 'wallet',
  });

  useEffect(() => { localStorage.setItem(CART_KEY, JSON.stringify(cart)); }, [cart]);

  useEffect(() => {
    API.get('/wallet/balance').then((res) => setWalletBalance(res.data?.balance || 0)).catch(() => {});
    API.get('/patients').then((res) => setPatients(res.data || [])).catch(() => {});
    API.get('/b2b/coupons/all-active').then((res) => setAvailableCoupons(res.data || [])).catch(() => {});
  }, []);

  const vendorOptions = Array.from(
    new Map((items || []).map((i) => [i.vendor_id, i.vendor_name])).entries()
  ).map(([id, name]) => ({ id, name }));

  const typeOptions = Array.from(new Set((items || []).map((i) => i.item_type)));

  const typeLabel = (t) => ({
    lab_test: 'Lab Tests', medicine: 'Medicines', inventory_product: 'Products', marketing_service: 'Services',
  }[t] || 'Other');

  const filteredItems = (items || []).filter((i) => {
    if (activeVendor !== 'all' && i.vendor_id !== activeVendor) return false;
    if (activeType !== 'all' && i.item_type !== activeType) return false;
    if (search && !i.name?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  useEffect(() => {
    const dismissed = sessionStorage.getItem('labs_store_popup_dismissed') === 'true';
    setShowPopup(!!popup?.image_url && !dismissed);
  }, [popup]);

  const dismissPopup = () => {
    setShowPopup(false);
    sessionStorage.setItem('labs_store_popup_dismissed', 'true');
  };

  const addToCart = (item) => {
    const idx = cart.findIndex((c) => c.item_id === item.item_id && c.vendor_id === item.vendor_id);
    if (idx >= 0) {
      const next = [...cart];
      next[idx].quantity += 1;
      next[idx].total_price = Math.round(next[idx].quantity * item.b2b_price * 100) / 100;
      setCart(next);
    } else {
      setCart([...cart, {
        item_id: item.item_id, item_type: item.item_type, item_name: item.name,
        vendor_id: item.vendor_id, vendor_name: item.vendor_name,
        vendor_gst_percentage: item.gst_percentage ?? 18,
        quantity: 1, unit_price: item.b2b_price, total_price: item.b2b_price,
        patient_mrp_unit: item.patient_mrp,
      }]);
    }
    toast.success(`Added ${item.name} to cart`);
  };

  const updateQty = (idx, delta) => {
    const next = [...cart];
    const newQty = next[idx].quantity + delta;
    if (newQty <= 0) {
      next.splice(idx, 1);
    } else {
      next[idx].quantity = newQty;
      next[idx].total_price = Math.round(newQty * next[idx].unit_price * 100) / 100;
    }
    setCart(next);
  };

  const removeFromCart = (idx) => setCart(cart.filter((_, i) => i !== idx));

  const getCartByVendor = () => {
    const grouped = {};
    cart.forEach((item) => {
      if (!grouped[item.vendor_id]) {
        grouped[item.vendor_id] = { vendor_id: item.vendor_id, vendor_name: item.vendor_name, vendor_gst_percentage: item.vendor_gst_percentage ?? 18, items: [] };
      }
      grouped[item.vendor_id].items.push(item);
    });
    return Object.values(grouped);
  };

  const selectPatient = (patient) => {
    setOrderDetails((od) => ({
      ...od,
      patient_id: String(patient.id || ''),
      patient_name: String(patient.name || ''),
      patient_phone: String(patient.phone || ''),
      patient_email: String(patient.email || ''),
      patient_age: String(patient.age || ''),
      patient_gender: String(patient.gender || ''),
    }));
    setShowPatientSelect(false);
  };

  const getCouponsForVendor = (vendorId) => availableCoupons.filter((c) => c.vendor_id === vendorId);

  const applyCoupon = async (vendorId, code) => {
    if (!code.trim()) return;
    setValidatingCoupon(true);
    try {
      const vendorCart = getCartByVendor().find((vc) => vc.vendor_id === vendorId);
      if (!vendorCart) { toast.error('No items from this vendor in cart'); return; }
      const orderValue = vendorCart.items.reduce((sum, i) => sum + i.total_price, 0);
      const res = await API.post(`/b2b/coupons/validate?vendor_id=${vendorId}&code=${code.toUpperCase()}&order_value=${orderValue}`);
      if (res.data.valid) {
        setAppliedCoupons((prev) => ({
          ...prev,
          [vendorId]: {
            code: res.data.code, discount_type: res.data.discount_type,
            discount_value: res.data.discount_value, calculated_discount: res.data.calculated_discount,
          }
        }));
        toast.success(`Coupon ${res.data.code} applied! Save ₹${res.data.calculated_discount}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid coupon');
    } finally {
      setValidatingCoupon(false);
    }
  };

  const removeCoupon = (vendorId) => {
    const next = { ...appliedCoupons };
    delete next[vendorId];
    setAppliedCoupons(next);
  };

  const cartSubtotal = cart.reduce((sum, i) => sum + i.total_price, 0);
  const couponDiscount = Object.values(appliedCoupons).reduce((sum, c) => sum + (c.calculated_discount || 0), 0);
  const afterCouponDiscount = cartSubtotal - couponDiscount;
  const cartTax = orderDetails.apply_gst ? getCartByVendor().reduce((sum, vc) => {
    const vcSubtotal = vc.items.reduce((s, i) => s + i.total_price, 0);
    const vcCoupon = appliedCoupons[vc.vendor_id]?.calculated_discount || 0;
    return sum + Math.round((vcSubtotal - vcCoupon) * (vc.vendor_gst_percentage ?? 18) / 100);
  }, 0) : 0;
  const cartTotal = Math.round((afterCouponDiscount + cartTax) * 100) / 100;

  const placeOrder = async (vendorCart) => {
    const vendorTotal = vendorCart.items.reduce((sum, i) => sum + i.total_price, 0);
    const appliedCoupon = appliedCoupons[vendorCart.vendor_id];
    const orderData = {
      vendor_id: vendorCart.vendor_id,
      items: vendorCart.items.map((i) => ({
        item_id: i.item_id, item_type: i.item_type, item_name: i.item_name,
        quantity: i.quantity, unit_price: i.unit_price, total_price: i.total_price,
      })),
      patient_id: orderDetails.patient_id || null,
      patient_name: orderDetails.patient_name || '',
      patient_phone: orderDetails.patient_phone || '',
      patient_email: orderDetails.patient_email || '',
      patient_age: orderDetails.patient_age ? String(orderDetails.patient_age) : '',
      patient_gender: orderDetails.patient_gender || '',
      delivery_address: orderDetails.delivery_address || '',
      delivery_phone: orderDetails.delivery_phone || '',
      delivery_location: orderDetails.delivery_location || null,
      notes: orderDetails.notes || '',
      gst_percent: vendorCart.vendor_gst_percentage ?? 18,
      apply_gst: orderDetails.apply_gst === true,
      coupon_code: appliedCoupon?.code || null,
      coupon_discount: appliedCoupon?.calculated_discount || 0,
      payment_method: orderDetails.payment_method || 'wallet',
    };
    await API.post('/b2b/orders', orderData);
    const method = orderDetails.payment_method === 'credit' ? 'on credit' : 'from wallet';
    toast.success(`Order placed with ${vendorCart.vendor_name}. ₹${vendorTotal.toLocaleString()} ${method}.`);
  };

  const placeAllOrders = async () => {
    if (orderDetails.payment_method !== 'credit' && cartTotal > walletBalance) {
      toast.error(`Insufficient wallet balance. Required: ₹${cartTotal.toLocaleString()}, Available: ₹${walletBalance.toLocaleString()}`);
      return;
    }
    setPlacingOrder(true);
    const vendorGroups = getCartByVendor();
    const succeededVendorIds = [];
    const failedVendors = [];

    for (const vc of vendorGroups) {
      try {
        await placeOrder(vc);
        succeededVendorIds.push(vc.vendor_id);
      } catch (err) {
        failedVendors.push({ name: vc.vendor_name, detail: err.response?.data?.detail || 'Order failed' });
      }
    }

    if (succeededVendorIds.length > 0) {
      setCart((prev) => prev.filter((c) => !succeededVendorIds.includes(c.vendor_id)));
      const nextCoupons = { ...appliedCoupons };
      succeededVendorIds.forEach((vid) => delete nextCoupons[vid]);
      setAppliedCoupons(nextCoupons);
      API.get('/wallet/balance').then((res) => setWalletBalance(res.data?.balance || 0)).catch(() => {});
    }
    if (failedVendors.length > 0) {
      toast.error(failedVendors.map((f) => `${f.name}: ${f.detail}`).join(', '));
    }
    if (succeededVendorIds.length > 0 && failedVendors.length === 0) {
      setShowOrderDialog(false);
      setOrderDetails({
        patient_id: '', patient_name: '', patient_phone: '', patient_email: '',
        patient_age: '', patient_gender: '', delivery_address: '', delivery_phone: '',
        delivery_location: null, notes: '', apply_gst: false, payment_method: 'wallet',
      });
    }
    if (succeededVendorIds.length > 0) onPurchased?.();
    setPlacingOrder(false);
  };

  if (loading) {
    return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-teal-600" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="labs-store-catalog-tab">
      {showPopup && popup?.image_url && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="labs-store-ad-popup">
          <div className="relative max-w-md w-full bg-white rounded-2xl overflow-hidden shadow-2xl">
            <button className="absolute top-2 right-2 bg-white/90 rounded-full p-1.5 z-10" onClick={dismissPopup} data-testid="labs-store-popup-close"><X className="w-4 h-4" /></button>
            <a href={popup.link_url || '#'} target={popup.link_url ? '_blank' : undefined} rel="noreferrer">
              <img src={popup.image_url} alt={popup.title || 'Ad'} className="w-full h-auto" />
            </a>
            {popup.title && <p className="p-3 text-sm text-center text-slate-600">{popup.title}</p>}
          </div>
        </div>
      )}

      {banners?.length > 0 && (
        <div className="relative rounded-2xl overflow-hidden h-40 sm:h-56 bg-gradient-to-r from-teal-600 to-cyan-600" data-testid="labs-store-banner-carousel">
          <a href={banners[bannerIdx]?.link_url || '#'} target={banners[bannerIdx]?.link_url ? '_blank' : undefined} rel="noreferrer">
            <img src={banners[bannerIdx]?.image_url} alt={banners[bannerIdx]?.title || ''} className="w-full h-full object-cover" />
          </a>
          {banners.length > 1 && (
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
              {banners.map((_, i) => (
                <button key={i} onClick={() => setBannerIdx(i)} className={`w-2 h-2 rounded-full ${i === bannerIdx ? 'bg-white' : 'bg-white/50'}`} data-testid={`labs-store-banner-dot-${i}`} />
              ))}
            </div>
          )}
        </div>
      )}

      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Search tests, products..." className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="labs-store-item-search" />
        </div>
        <Button variant="outline" className="relative" onClick={() => setShowCart(true)} data-testid="labs-store-cart-btn">
          <ShoppingCart className="w-4 h-4 mr-2" /> Cart
          {cart.length > 0 && (
            <span className="absolute -top-2 -right-2 bg-teal-600 text-white text-[10px] rounded-full w-5 h-5 flex items-center justify-center" data-testid="labs-store-cart-count">
              {cart.reduce((s, i) => s + i.quantity, 0)}
            </span>
          )}
        </Button>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-16 text-slate-400">
          <ShoppingBag className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p>No featured items yet. Check back soon!</p>
        </div>
      ) : (
        <>
          {typeOptions.length > 1 && (
            <div className="flex gap-2 flex-wrap" data-testid="labs-store-type-filter">
              <Button size="sm" variant={activeType === 'all' ? 'default' : 'outline'} onClick={() => setActiveType('all')} data-testid="labs-store-type-filter-all">All Types</Button>
              {typeOptions.map((t) => (
                <Button key={t} size="sm" variant={activeType === t ? 'default' : 'outline'} onClick={() => setActiveType(t)} data-testid={`labs-store-type-filter-${t}`}>
                  {typeLabel(t)}
                </Button>
              ))}
            </div>
          )}
          {vendorOptions.length > 1 && (
            <div className="flex gap-2 flex-wrap" data-testid="labs-store-vendor-filter">
              <Button size="sm" variant={activeVendor === 'all' ? 'default' : 'outline'} onClick={() => setActiveVendor('all')} data-testid="labs-store-vendor-filter-all">All Vendors</Button>
              {vendorOptions.map((v) => (
                <Button key={v.id} size="sm" variant={activeVendor === v.id ? 'default' : 'outline'} onClick={() => setActiveVendor(v.id)} data-testid={`labs-store-vendor-filter-${v.id}`}>
                  {v.name}
                </Button>
              ))}
            </div>
          )}
          {filteredItems.length === 0 ? (
            <p className="text-center py-10 text-slate-400">No items match your filters.</p>
          ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredItems.map((item) => (
            <Card key={item.featured_id} className="overflow-hidden hover:shadow-lg transition-shadow" data-testid={`labs-store-item-${item.featured_id}`}>
              <div className="h-36 bg-slate-100 flex items-center justify-center overflow-hidden">
                {item.photo_url ? (
                  <img src={item.photo_url} alt={item.name} className="w-full h-full object-cover" />
                ) : item.item_type === 'lab_test' ? (
                  <FlaskConical className="w-10 h-10 text-teal-400" />
                ) : (
                  <Package className="w-10 h-10 text-teal-400" />
                )}
              </div>
              <CardContent className="p-4 space-y-2">
                <Badge variant="secondary" className="text-xs">{item.vendor_name}</Badge>
                <h3 className="font-semibold text-slate-900 line-clamp-1">{item.name}</h3>
                <div className="grid grid-cols-3 gap-1 text-xs text-center bg-slate-50 rounded-lg p-2">
                  <div><p className="text-slate-400">MRP</p><p className={`font-semibold ${item.mrp !== item.patient_mrp ? 'line-through text-slate-400' : 'text-slate-600'}`}>₹{item.mrp}</p></div>
                  <div><p className="text-slate-400">Patient MRP</p><p className="font-semibold">₹{item.patient_mrp}</p></div>
                  <div><p className="text-slate-400">Your Price</p><p className="font-bold text-teal-700">₹{item.b2b_price}</p></div>
                </div>
                <Button className="w-full" size="sm" onClick={() => addToCart(item)} data-testid={`labs-store-buy-btn-${item.featured_id}`}>
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add to Cart
                </Button>
              </CardContent>
            </Card>
          ))}
          </div>
          )}
        </>
      )}

      {/* Cart Sidebar - matches Marketplace's cart drawer */}
      {showCart && (
        <div className="fixed inset-0 z-50 bg-black/40" onClick={() => setShowCart(false)} data-testid="labs-store-cart-drawer">
          <div className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-bold">Shopping Cart ({cart.length})</h2>
              <button onClick={() => setShowCart(false)} className="p-2 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {cart.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <ShoppingCart className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Your cart is empty</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {getCartByVendor().map((vc) => (
                    <div key={vc.vendor_id} className="border rounded-lg p-3">
                      <p className="font-medium text-sm text-slate-600 mb-2"><Building2 className="w-4 h-4 inline mr-1" />{vc.vendor_name}</p>
                      {vc.items.map((item) => {
                        const idx = cart.findIndex((c) => c.item_id === item.item_id && c.vendor_id === item.vendor_id);
                        return (
                          <div key={item.item_id} className="flex items-center justify-between py-2 border-t" data-testid={`labs-store-cart-item-${item.item_id}`}>
                            <div className="flex-1">
                              <p className="text-sm font-medium">{item.item_name}</p>
                              <p className="text-xs text-slate-500">₹{item.unit_price} each</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <button onClick={() => updateQty(idx, -1)} className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center" data-testid={`labs-store-cart-dec-${item.item_id}`}><Minus className="w-3 h-3" /></button>
                              <span className="w-8 text-center">{item.quantity}</span>
                              <button onClick={() => updateQty(idx, 1)} className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center" data-testid={`labs-store-cart-inc-${item.item_id}`}><Plus className="w-3 h-3" /></button>
                              <button onClick={() => removeFromCart(idx)} className="w-6 h-6 rounded-full bg-red-100 text-red-600 flex items-center justify-center ml-2" data-testid={`labs-store-cart-remove-${item.item_id}`}><Trash2 className="w-3 h-3" /></button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              )}
            </div>
            {cart.length > 0 && (
              <div className="border-t bg-white p-4">
                <div className="space-y-1 mb-3 text-sm">
                  <div className="flex justify-between"><span>Subtotal</span><span>₹{cartSubtotal.toLocaleString()}</span></div>
                  {couponDiscount > 0 && <div className="flex justify-between text-green-600"><span>Coupon Discount</span><span>-₹{couponDiscount.toLocaleString()}</span></div>}
                  <div className="flex justify-between text-slate-500"><span>GST</span><span>₹{cartTax.toLocaleString()}</span></div>
                  <div className="flex justify-between font-bold text-lg pt-1 border-t"><span>Total</span><span>₹{cartTotal.toLocaleString()}</span></div>
                </div>
                <div className="mb-3 p-2 rounded-lg bg-slate-50 border flex items-center justify-between">
                  <div className="flex items-center gap-2"><Wallet className="w-4 h-4 text-teal-600" /><span className="text-sm text-slate-600">Wallet: ₹{walletBalance.toLocaleString()}</span></div>
                  <Button variant="link" size="sm" className="text-teal-600 h-auto p-0" onClick={() => window.location.href = '/doctor/wallet'}>Add Funds</Button>
                </div>
                <Button className="w-full" onClick={() => { setShowCart(false); setShowOrderDialog(true); }} data-testid="labs-store-proceed-checkout-btn">
                  Proceed to Checkout
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Order Dialog - matches Marketplace's "Complete Your Order" exactly */}
      <Dialog open={showOrderDialog} onOpenChange={setShowOrderDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="labs-store-order-dialog">
          <DialogHeader><DialogTitle>Complete Your Order</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Patient Details</Label>
                <Button size="sm" variant="outline" onClick={() => setShowPatientSelect(true)} data-testid="labs-store-select-patient-btn">
                  <User className="w-4 h-4 mr-1" /> Select Patient
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Patient Name" value={orderDetails.patient_name} onChange={(e) => setOrderDetails({ ...orderDetails, patient_name: e.target.value })} data-testid="labs-store-order-patient-name" />
                <Input placeholder="Phone" value={orderDetails.patient_phone} onChange={(e) => setOrderDetails({ ...orderDetails, patient_phone: e.target.value })} data-testid="labs-store-order-patient-phone" />
                <Input placeholder="Email" value={orderDetails.patient_email} onChange={(e) => setOrderDetails({ ...orderDetails, patient_email: e.target.value })} />
                <div className="flex gap-2">
                  <Input placeholder="Age" value={orderDetails.patient_age} onChange={(e) => setOrderDetails({ ...orderDetails, patient_age: e.target.value })} className="w-20" />
                  <Select value={orderDetails.patient_gender} onValueChange={(v) => setOrderDetails({ ...orderDetails, patient_gender: v })}>
                    <SelectTrigger><SelectValue placeholder="Gender" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Delivery Address & Phone</Label>
              <AddressPickerCheckout
                token={localStorage.getItem('clinic_token')}
                initialAddress={orderDetails.delivery_address}
                initialPhone={orderDetails.delivery_phone}
                initialLocation={orderDetails.delivery_location}
                onAddressSelected={(data) => {
                  setOrderDetails((od) => ({ ...od, delivery_address: data.address || '', delivery_phone: data.phone || '', delivery_location: data.location || null }));
                }}
              />
            </div>

            <div className="space-y-3 p-3 bg-green-50 rounded-lg border border-green-200">
              <Label className="text-green-700 flex items-center gap-2"><Tag className="w-4 h-4" /> Apply Vendor Coupons</Label>
              {getCartByVendor().map((vendorCart) => {
                const vendorCoupons = getCouponsForVendor(vendorCart.vendor_id);
                const appliedCoupon = appliedCoupons[vendorCart.vendor_id];
                const vendorSubtotal = vendorCart.items.reduce((sum, i) => sum + i.total_price, 0);
                return (
                  <div key={vendorCart.vendor_id} className="bg-white rounded-lg p-3 border border-green-100">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-slate-700">{vendorCart.vendor_name}</span>
                      <span className="text-xs text-slate-500">Subtotal: ₹{vendorSubtotal}</span>
                    </div>
                    {appliedCoupon ? (
                      <div className="flex items-center justify-between bg-green-100 rounded p-2">
                        <div><span className="font-bold text-green-700">{appliedCoupon.code}</span><span className="text-sm text-green-600 ml-2">-₹{appliedCoupon.calculated_discount}</span></div>
                        <Button size="sm" variant="ghost" className="h-6 text-red-500" onClick={() => removeCoupon(vendorCart.vendor_id)}><X className="w-4 h-4" /></Button>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <div className="flex gap-2">
                          <Input placeholder="Enter coupon code" className="flex-1 h-8 text-sm" onKeyDown={(e) => e.key === 'Enter' && applyCoupon(vendorCart.vendor_id, e.target.value)} id={`coupon-input-${vendorCart.vendor_id}`} />
                          <Button size="sm" variant="outline" className="h-8" disabled={validatingCoupon} onClick={() => applyCoupon(vendorCart.vendor_id, document.getElementById(`coupon-input-${vendorCart.vendor_id}`).value)}>
                            Apply
                          </Button>
                        </div>
                        {vendorCoupons.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {vendorCoupons.slice(0, 3).map((c) => (
                              <button key={c.id} className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded hover:bg-green-200 transition-colors" onClick={() => applyCoupon(vendorCart.vendor_id, c.code)}>
                                {c.code}: {c.discount_type === 'percentage' ? `${c.discount_value}%` : `₹${c.discount_value}`} off
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="space-y-2 p-3 bg-blue-50 rounded-lg">
              <div className="flex items-center justify-between">
                <Label className="text-blue-700">GST Settings</Label>
                <div className="flex items-center gap-2">
                  <Switch checked={orderDetails.apply_gst} onCheckedChange={(v) => setOrderDetails({ ...orderDetails, apply_gst: v })} data-testid="labs-store-gst-toggle" />
                  <span className="text-sm">{orderDetails.apply_gst ? 'Enabled' : 'Disabled'}</span>
                </div>
              </div>
              {orderDetails.apply_gst && (
                <div className="space-y-1">
                  {getCartByVendor().map((vc) => (
                    <div key={vc.vendor_id} className="flex items-center gap-2 text-sm text-blue-700">
                      <span>{vc.vendor_name}:</span><span className="font-semibold">{vc.vendor_gst_percentage ?? 18}% GST</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Textarea placeholder="Notes" value={orderDetails.notes} onChange={(e) => setOrderDetails({ ...orderDetails, notes: e.target.value })} />

            <div className="bg-slate-50 rounded-lg p-3">
              <p className="font-medium mb-2">Order Summary</p>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between"><span>{cart.length} items</span><span>₹{cartSubtotal.toLocaleString()}</span></div>
                {couponDiscount > 0 && <div className="flex justify-between text-green-600"><span className="flex items-center gap-1"><Tag className="w-3 h-3" /> Coupon Discount</span><span>-₹{couponDiscount.toLocaleString()}</span></div>}
                <div className="flex justify-between text-slate-500"><span>GST</span><span>₹{cartTax.toLocaleString()}</span></div>
                <div className="flex justify-between font-bold pt-1 border-t"><span>Total to Pay</span><span>₹{cartTotal.toLocaleString()}</span></div>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">Payment Method</Label>
              <div className="grid grid-cols-2 gap-3">
                <button type="button" className={`p-3 rounded-lg border-2 text-left transition-colors ${orderDetails.payment_method === 'wallet' ? 'border-teal-500 bg-teal-50' : 'border-slate-200 hover:border-slate-300'}`} onClick={() => setOrderDetails({ ...orderDetails, payment_method: 'wallet' })} data-testid="labs-store-payment-wallet-btn">
                  <div className="flex items-center gap-2">
                    <Wallet className="w-5 h-5 text-teal-600" />
                    <div><p className="font-medium text-sm">Pay via Wallet</p><p className="text-xs text-slate-500">Balance: ₹{walletBalance.toLocaleString()}</p></div>
                  </div>
                </button>
                <button type="button" className={`p-3 rounded-lg border-2 text-left transition-colors ${orderDetails.payment_method === 'credit' ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-slate-300'}`} onClick={() => setOrderDetails({ ...orderDetails, payment_method: 'credit' })} data-testid="labs-store-payment-credit-btn">
                  <div className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-blue-600" />
                    <div><p className="font-medium text-sm">Buy on Credit</p><p className="text-xs text-slate-500">Pay later (vendor terms)</p></div>
                  </div>
                </button>
              </div>
              {orderDetails.payment_method === 'wallet' && cartTotal > walletBalance && (
                <div className="p-2 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between">
                  <p className="text-xs text-red-600">Insufficient wallet balance</p>
                  <Button variant="outline" size="sm" className="text-red-600 border-red-300 h-7 text-xs" onClick={() => window.location.href = '/doctor/wallet'}>Add Funds</Button>
                </div>
              )}
              {orderDetails.payment_method === 'credit' && (
                <p className="text-xs text-blue-600 bg-blue-50 p-2 rounded">Amount will be auto-debited from your wallet after the vendor's credit period. If insufficient funds, portal access will be restricted.</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOrderDialog(false)}>Cancel</Button>
            <Button onClick={placeAllOrders} disabled={placingOrder || (orderDetails.payment_method === 'wallet' && cartTotal > walletBalance)} className="bg-teal-700 hover:bg-teal-800" data-testid="labs-store-cart-checkout-btn">
              {placingOrder ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Placing Order...</>
              ) : orderDetails.payment_method === 'credit' ? (
                <><FileText className="w-4 h-4 mr-2" /> Order on Credit ₹{cartTotal.toLocaleString()}</>
              ) : cartTotal > walletBalance ? (
                <><Lock className="w-4 h-4 mr-2" /> Add Funds First</>
              ) : (
                <><Wallet className="w-4 h-4 mr-2" /> Pay ₹{cartTotal.toLocaleString()}</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showPatientSelect} onOpenChange={setShowPatientSelect}>
        <DialogContent data-testid="labs-store-patient-select-dialog">
          <DialogHeader><DialogTitle>Select Patient</DialogTitle></DialogHeader>
          <div className="max-h-60 overflow-y-auto space-y-2">
            {patients.map((patient) => (
              <div key={patient.id} className="p-3 border rounded-lg hover:bg-slate-50 cursor-pointer" onClick={() => selectPatient(patient)} data-testid={`labs-store-patient-option-${patient.id}`}>
                <p className="font-medium">{patient.name}</p>
                <p className="text-sm text-slate-500">{patient.phone} • {patient.age} • {patient.gender}</p>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
